import { useState } from 'react';
import { X, Calendar, Users, Clock, FileText, Check, MapPin } from 'lucide-react';
import { useUser } from '../context/user-context';
import { useTables } from '../context/tables-context';
import { useBranch } from '../context/branch-context';
import { useUiAlert } from '../context/ui-alert-context';
import { useTextos } from '../context/regional-context';
import { textosReservas } from '../../i18n/modulos/reservas';
import { localeFechas } from '../../i18n/actual';

interface QuickReservationModalProps {
 isOpen: boolean;
 onClose: () => void;
 preselectedTableId?: string; // Nueva prop para mesa preseleccionada
}

export function QuickReservationModal({ isOpen, onClose, preselectedTableId }: QuickReservationModalProps) {
 const { user } = useUser();
 const { addFutureReservation, getAvailableTables, getTableById, getTablesByBranch } = useTables();
 const { currentBranch } = useBranch();
 const ui = useUiAlert();
 const [step, setStep] = useState(1);
 const [people, setPeople] = useState('');
 const [date, setDate] = useState('');
 const [time, setTime] = useState('');
 const [reason, setReason] = useState('');
 const [selectedTable, setSelectedTable] = useState<number | null>(preselectedTableId ? parseInt(preselectedTableId) : null);
 const [customerName, setCustomerName] = useState('');
 const [customerPhone, setCustomerPhone] = useState('');
 const tx = useTextos(textosReservas);

 if (!isOpen) return null;

 const handleClose = () => {
 setStep(1);
 setPeople('');
 setDate('');
 setTime('');
 setReason('');
 setSelectedTable(preselectedTableId ? parseInt(preselectedTableId) : null);
 setCustomerName('');
 setCustomerPhone('');
 onClose();
 };

 const peopleNum = parseInt(people) || 0;
 
 // Filtrar mesas disponibles por la sede actual
 const branchTables = getTablesByBranch(currentBranch);
 const availableTables = branchTables.filter(t => t.status === 'available');
 const suggestedTables = availableTables.filter(
 t => t.seats >= peopleNum && t.seats <= peopleNum + 2
 );

 // Generar ID de reserva
 const generateReservationId = () => {
 return `RES-${Math.floor(100 + Math.random() * 900)}`;
 };

 const handleConfirm = () => {
 if (!selectedTable) return;
 
 const reservationId = generateReservationId();
 
 // Calcular minutos hasta la reserva (simulación: 120 minutos)
 const reservationMinutes = 120; // En producción, calcular basándose en fecha/hora
 
 // Crear reserva futura sin ocupar la mesa
 addFutureReservation(selectedTable, reservationId, time, customerName, customerPhone, reservationMinutes);
 
 const tableInfo = getTableById(selectedTable);
 ui.toast({
 level: 'success',
 title: tx('qrReservaConfirmada', { id: reservationId }),
 description: tx('qrToastDescripcion', { cliente: customerName, ciudad: tableInfo?.city || tx('qrSedeFallback'), mesa: selectedTable, fecha: date, hora: time, personas: people }),
 durationMs: 6000,
 });
 handleClose();
 };

 return (
 <div
 className="fixed inset-0 flex items-center justify-center z-50 p-0 sm:p-4 animate-apple-fade-in"
 style={{ background: 'rgba(14,22,38,.55)', backdropFilter: 'blur(10px)' }}
 >
 <div
 className="w-full max-w-3xl h-full sm:h-auto sm:max-h-[90vh] overflow-hidden flex flex-col sk-modal-shell"
 style={{ background: 'var(--surface)', boxShadow: 'var(--sh-lg)' }}
 >
 <div
 className="flex items-center justify-between p-6"
 style={{ background: 'var(--blue-grad)', color: '#fff' }}
 >
 <div className="flex items-center gap-3">
 <div
 className="flex items-center justify-center"
 style={{
 width: 44, height: 44,
 background: 'rgba(255,255,255,.16)',
 borderRadius: 'var(--r-sm)',
 }}
 >
 <Calendar className="w-6 h-6" />
 </div>
 <h2 style={{ fontSize: 22, fontWeight: 800, letterSpacing: '-.01em' }}>
 {tx('nuevaReserva')}
 </h2>
 </div>
 <button
 onClick={handleClose}
 className="press"
 style={{
 padding: 10,
 background: 'rgba(255,255,255,.12)',
 border: 0,
 borderRadius: 'var(--r-sm)',
 color: '#fff',
 cursor: 'pointer',
 }}
 >
 <X className="w-5 h-5" />
 </button>
 </div>

 <div className="flex-1 overflow-y-auto p-6">
 {/* Step 1: Datos del cliente y cantidad de personas */}
 {step === 1 && (
 <div>
 <h3 className="text-xl mb-2">{tx('qrInfoCliente')}</h3>
 <p className="text-gray-600 mb-4">{tx('qrSedeLabel')} {currentBranch}</p>

 <div className="space-y-4 mb-6">
 <div>
 <label className="block text-gray-700 mb-2">{tx('qrNombreCliente')}</label>
 <input
 type="text"
 value={customerName}
 onChange={(e) => setCustomerName(e.target.value)}
 className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary/40"
 placeholder={tx('qrNombreCompleto')}
 />
 </div>

 <div>
 <label className="block text-gray-700 mb-2">{tx('telefono')}</label>
 <input
 type="tel"
 value={customerPhone}
 onChange={(e) => setCustomerPhone(e.target.value)}
 className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary/40"
 placeholder="310-555-0123"
 />
 </div>

 <div>
 <label className="block text-gray-700 mb-2 flex items-center gap-2">
 <Users className="w-5 h-5" />
 {tx('qrCantidadPersonas')}
 </label>
 <input
 type="number"
 value={people}
 onChange={(e) => setPeople(e.target.value)}
 className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary/40 text-2xl text-center"
 placeholder={tx('qrEj4')}
 min="1"
 />
 </div>
 </div>

 <div className="flex gap-3 sticky bottom-0 bg-white pt-4 border-t border-gray-200 -mb-6 -mx-6 px-6 pb-6">
 <button
 onClick={handleClose}
 className="flex-1 px-6 py-3 border border-gray-200 rounded-full hover:bg-gray-50"
 >
 {tx('cancelar')}
 </button>
 <button
 onClick={() => setStep(2)}
 disabled={!people || !customerName || !customerPhone}
 className="flex-1 px-6 py-3 bg-primary text-white rounded-full hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed press shadow-apple ring-1 ring-primary/15"
 >
 {tx('qrContinuar')}
 </button>
 </div>
 </div>
 )}

 {/* Step 2: Fecha y Hora */}
 {step === 2 && (
 <div>
 <h3 className="text-xl mb-4">{tx('qrFechaHoraReserva')}</h3>

 <div className="space-y-4 mb-6">
 <div>
 <label className="block text-gray-700 mb-2 flex items-center gap-2">
 <Calendar className="w-5 h-5" />
 {tx('fecha')}
 </label>
 <input
 type="date"
 value={date}
 onChange={(e) => setDate(e.target.value)}
 className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary/40"
 min={new Date().toISOString().split('T')[0]}
 />
 </div>

 <div>
 <label className="block text-gray-700 mb-2 flex items-center gap-2">
 <Clock className="w-5 h-5" />
 {tx('hora')}
 </label>
 <input
 type="time"
 value={time}
 onChange={(e) => setTime(e.target.value)}
 className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary/40"
 />
 </div>
 </div>

 <div className="flex gap-3">
 <button
 onClick={() => setStep(1)}
 className="flex-1 px-6 py-3 border border-gray-200 rounded-full hover:bg-gray-50"
 >
 {tx('qrAtras')}
 </button>
 <button
 onClick={() => setStep(3)}
 disabled={!date || !time}
 className="flex-1 px-6 py-3 bg-primary text-white rounded-full hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed press shadow-apple ring-1 ring-primary/15"
 >
 {tx('qrContinuar')}
 </button>
 </div>
 </div>
 )}

 {/* Step 3: Motivo y Asignar Mesa */}
 {step === 3 && (
 <div>
 <h3 className="text-xl mb-4">{tx('qrMotivoYMesa')}</h3>

 <div className="space-y-4 mb-6">
 <div>
 <label className="block text-gray-700 mb-2 flex items-center gap-2">
 <FileText className="w-5 h-5" />
 {tx('qrMotivoOpcional')}
 </label>
 <input
 type="text"
 value={reason}
 onChange={(e) => setReason(e.target.value)}
 className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary/40"
 placeholder={tx('qrMotivoPlaceholder')}
 />
 </div>

 {!preselectedTableId && (
 <div>
 <label className="block text-gray-700 mb-2 flex items-center gap-2">
 <MapPin className="w-5 h-5" />
 {tx('qrSeleccionaMesa')}
 </label>
 <p className="text-sm text-gray-500 mb-3">
 {tx('qrMesasSugeridas', { n: people })}
 </p>
 
 <div className="grid grid-cols-3 gap-3 max-h-64 overflow-y-auto">
 {suggestedTables.map(table => (
 <button
 key={table.id}
 onClick={() => setSelectedTable(table.id)}
 className={`p-4 border-2 rounded-lg transition-all ${
 selectedTable === table.id
 ? 'border-primary bg-secondary'
 : 'border-gray-200 hover:border-primary'
 }`}
 >
 <div className="text-2xl mb-1">🏛️</div>
 <div className="text-sm font-semibold">{table.city}</div>
 <div className="text-xs text-gray-500">{tx('qrMesa')} {table.id}</div>
 <div className="text-xs text-gray-500 mt-1">
 <Users className="w-3 h-3 inline mr-1" />
 {table.seats} {tx('qrPuestos')}
 </div>
 </button>
 ))}
 </div>

 {suggestedTables.length === 0 && (
 <div className="text-center py-8 text-gray-500">
 <p>{tx('qrSinMesas', { n: people })}</p>
 <p className="text-sm mt-2">{tx('qrOtraCantidad')}</p>
 </div>
 )}
 </div>
 )}

 {preselectedTableId && selectedTable && (
 <div className="bg-secondary border border-blue-200 rounded-lg p-4">
 <p className="text-gray-700 mb-1">{tx('qrMesaPreseleccionada')}</p>
 <p className="text-xl text-gray-900">
 {getTableById(selectedTable)?.city} ({tx('qrMesa')} {selectedTable})
 </p>
 </div>
 )}
 </div>

 <div className="flex gap-3">
 <button
 onClick={() => setStep(2)}
 className="flex-1 px-6 py-3 border border-gray-200 rounded-full hover:bg-gray-50"
 >
 {tx('qrAtras')}
 </button>
 <button
 onClick={() => setStep(4)}
 disabled={!selectedTable}
 className="flex-1 px-6 py-3 bg-primary text-white rounded-full hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed press shadow-apple ring-1 ring-primary/15"
 >
 {tx('qrVerResumen')}
 </button>
 </div>
 </div>
 )}

 {/* Step 4: Resumen */}
 {step === 4 && (
 <div>
 <h3 className="text-xl mb-4">{tx('qrResumen')}</h3>

 <div className="bg-secondary p-6 rounded-xl mb-6 space-y-3">
 <div className="flex items-start gap-3">
 <MapPin className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
 <div>
 <div className="text-sm text-gray-600">{tx('qrSede')}</div>
 <div className="text-lg">{currentBranch}</div>
 </div>
 </div>

 <div className="flex items-start gap-3">
 <Users className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
 <div>
 <div className="text-sm text-gray-600">{tx('cliente')}</div>
 <div className="text-lg">{customerName}</div>
 <div className="text-sm text-gray-500">{customerPhone}</div>
 </div>
 </div>

 <div className="flex items-start gap-3">
 <Users className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
 <div>
 <div className="text-sm text-gray-600">{tx('qrCantidadPersonas')}</div>
 <div className="text-lg">{people} {tx('qrPersonas')}</div>
 </div>
 </div>

 <div className="flex items-start gap-3">
 <Calendar className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
 <div>
 <div className="text-sm text-gray-600">{tx('qrFechaYHora')}</div>
 <div className="text-lg">
 {new Date(date + 'T12:00:00Z').toLocaleDateString(localeFechas(), { timeZone: 'UTC',
 weekday: 'long', 
 year: 'numeric', 
 month: 'long', 
 day: 'numeric' 
 })} - {time}
 </div>
 </div>
 </div>

 <div className="flex items-start gap-3">
 <MapPin className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
 <div>
 <div className="text-sm text-gray-600">{tx('qrMesaAsignada')}</div>
 <div className="text-lg">
 {getTableById(selectedTable!)?.city} ({tx('qrMesa')} {selectedTable})
 </div>
 </div>
 </div>

 {reason && (
 <div className="flex items-start gap-3">
 <FileText className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
 <div>
 <div className="text-sm text-gray-600">{tx('qrMotivo')}</div>
 <div className="text-lg">{reason}</div>
 </div>
 </div>
 )}
 </div>

 <div className="bg-green-50 border border-green-200 p-4 rounded-lg mb-6">
 <div className="flex items-center gap-2 text-green-700">
 <Check className="w-5 h-5" />
 <span>{tx('qrAvisoConfirmacion')}</span>
 </div>
 </div>

 <div className="flex gap-3">
 <button
 onClick={() => setStep(3)}
 className="flex-1 px-6 py-3 border border-gray-200 rounded-full hover:bg-gray-50"
 >
 {tx('qrAtras')}
 </button>
 <button
 onClick={handleConfirm}
 className="flex-1 px-6 py-3 bg-green-600 text-white rounded-full hover:bg-green-700 flex items-center justify-center gap-2 press shadow-apple ring-1 ring-green-600/15"
 >
 <Check className="w-5 h-5" />
 {tx('qrConfirmarReserva')}
 </button>
 </div>
 </div>
 )}
 </div>
 </div>
 </div>
 );
}